import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useI18n } from '../i18n';
import mpegts from 'mpegts.js';

type Props = {
  url?: string;
  muted?: boolean;
  className?: string;
  isLive?: boolean;
  label?: string;
  videoOnly?: boolean;
  mediaShardKey?: string | number;
  volume?: number;
  playbackRate?: number;
  onStatusChange?: (status: PlayerStatus) => void;
  onAudioUnsupported?: (diagnostic: string) => void;
  onPlaybackPausedChange?: (paused: boolean) => void;
  showPlaybackToggle?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  onSkipBackward?: () => void;
  onSkipForward?: () => void;
  onStop?: () => void;
  onExitFullscreen?: () => void;
  previousDisabled?: boolean;
  nextDisabled?: boolean;
  skipDisabled?: boolean;
  stopDisabled?: boolean;
};

export type OapmPlayerHandle = {
  togglePlayback: () => void;
  play: () => void;
  pause: () => void;
  video: () => HTMLVideoElement | null;
};

export type PlayerStatus = 'idle' | 'connecting' | 'playing' | 'error';

const retryDelaysMs = [350, 800, 1_600, 2_800];

const mediaShardBases = String(import.meta.env.VITE_OAPM_MEDIA_SHARDS ?? '')
  .split(',')
  .map((value) => value.trim().replace(/\/$/, ''))
  .filter(Boolean);

function hashShardKey(value: string | number | undefined): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.abs(Math.trunc(value));
  }

  const text = String(value ?? '0');
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) - hash + text.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

function resolveMediaUrl(url: string, shardKey: string | number | undefined): string {
  if (mediaShardBases.length === 0 || !url.startsWith('/media/')) {
    return new URL(url, window.location.origin).toString();
  }

  const shardIndex = hashShardKey(shardKey) % mediaShardBases.length;
  const shardBase = mediaShardBases[shardIndex] ?? mediaShardBases[0];
  return new URL(url, `${shardBase}/`).toString();
}


function stringifyUnknown(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function summarizeMediaInfo(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return stringifyUnknown(value);
  const data = value as Record<string, unknown>;
  const fields = [
    ['video', data.videoCodec],
    ['audio', data.audioCodec],
    ['mime', data.mimeType],
    ['resolution', data.width && data.height ? `${data.width}x${data.height}` : undefined],
    ['fps', data.fps],
    ['profile', data.profile],
    ['level', data.level],
  ] as const;

  const summary = fields
    .flatMap(([key, field]) => field === undefined || field === null || field === '' ? [] : [`${key}=${String(field)}`])
    .join('; ');
  return summary || stringifyUnknown(value);
}

function isHttpStatusCodeInvalid(detail: unknown): boolean {
  const text = String(detail ?? '');
  return detail === mpegts.ErrorDetails.NETWORK_STATUS_CODE_INVALID ||
    text === 'HttpStatusCodeInvalid' ||
    text.includes('StatusCodeInvalid');
}

function isCodecUnsupported(detail: unknown): boolean {
  const text = String(detail ?? '');
  return detail === mpegts.ErrorDetails.MEDIA_CODEC_UNSUPPORTED ||
    text === 'CodecUnsupported' ||
    text.includes('CodecUnsupported');
}

export const OapmPlayer = forwardRef<OapmPlayerHandle, Props>(function OapmPlayer({
  url,
  muted = true,
  className,
  isLive = true,
  label,
  videoOnly = true,
  mediaShardKey,
  volume = 1,
  playbackRate = 1,
  onStatusChange,
  onAudioUnsupported,
  onPlaybackPausedChange,
  showPlaybackToggle = true,
  onPrevious,
  onNext,
  onSkipBackward,
  onSkipForward,
  onStop,
  onExitFullscreen,
  previousDisabled = false,
  nextDisabled = false,
  skipDisabled = false,
  stopDisabled = false,
}: Props, forwardedRef) {
  const { t } = useI18n();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const retryAttemptRef = useRef(0);
  const onStatusChangeRef = useRef(onStatusChange);
  const onAudioUnsupportedRef = useRef(onAudioUnsupported);
  const onPlaybackPausedChangeRef = useRef(onPlaybackPausedChange);
  const manualPausedRef = useRef(false);
  const controlsHideTimerRef = useRef<number | undefined>(undefined);
  const [restartToken, setRestartToken] = useState(0);
  const [status, setStatus] = useState<PlayerStatus>('idle');
  const [statusMessage, setStatusMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [diagnostic, setDiagnostic] = useState<string>();
  const [controlsVisible, setControlsVisible] = useState(false);
  const [manuallyPaused, setManuallyPaused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const featureSummary = useMemo(() => {
    const features = mpegts.getFeatureList();
    return [
      `mse=${features.msePlayback}`,
      `live=${features.mseLivePlayback}`,
      `h265=${features.mseH265Playback}`,
      `loader=${features.networkLoaderName}`,
    ].join('; ');
  }, []);

  useEffect(() => {
    retryAttemptRef.current = 0;
    setRestartToken(0);
    setDiagnostic(undefined);
    manualPausedRef.current = false;
    setManuallyPaused(false);
    setControlsVisible(false);
    onPlaybackPausedChangeRef.current?.(false);
  }, [mediaShardKey, url]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = muted;
    video.volume = Math.min(1, Math.max(0, volume));
  }, [muted, restartToken, url, videoOnly, volume]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const nextRate = Math.min(4, Math.max(0.25, playbackRate));
    video.defaultPlaybackRate = nextRate;
    video.playbackRate = nextRate;
  }, [playbackRate, restartToken, url, videoOnly]);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  useEffect(() => {
    onAudioUnsupportedRef.current = onAudioUnsupported;
  }, [onAudioUnsupported]);

  useEffect(() => {
    onPlaybackPausedChangeRef.current = onPlaybackPausedChange;
  }, [onPlaybackPausedChange]);

  useEffect(() => {
    onStatusChangeRef.current?.(status);
  }, [status]);

  function clearControlsHideTimer() {
    if (controlsHideTimerRef.current !== undefined) {
      window.clearTimeout(controlsHideTimerRef.current);
      controlsHideTimerRef.current = undefined;
    }
  }

  function hidePlaybackControls() {
    clearControlsHideTimer();
    setControlsVisible(false);
  }

  function revealPlaybackControls() {
    if (!url || !showPlaybackToggle) return;
    clearControlsHideTimer();
    setControlsVisible(true);
    controlsHideTimerRef.current = window.setTimeout(() => {
      controlsHideTimerRef.current = undefined;
      setControlsVisible(false);
    }, isFullscreen ? 1_900 : 1_350);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const root = rootRef.current;
    if (!root || !url || !showPlaybackToggle) return;

    const rect = root.getBoundingClientRect();
    const controlHotZone = isFullscreen ? 124 : 72;
    const distanceFromBottom = rect.bottom - event.clientY;

    if (distanceFromBottom <= controlHotZone) {
      revealPlaybackControls();
    } else if (controlsVisible) {
      hidePlaybackControls();
    }
  }

  function setManualPaused(next: boolean) {
    manualPausedRef.current = next;
    setManuallyPaused(next);
    onPlaybackPausedChangeRef.current?.(next);
    revealPlaybackControls();
  }

  function togglePlayback() {
    const video = videoRef.current;
    if (!video || status !== 'playing') return;

    if (manualPausedRef.current || video.paused) {
      manualPausedRef.current = false;
      setManuallyPaused(false);
      onPlaybackPausedChangeRef.current?.(false);
      void Promise.resolve(video.play())
        .then(() => {
          setStatus('playing');
          setStatusMessage(undefined);
          setError(undefined);
          revealPlaybackControls();
        })
        .catch((reason: unknown) => {
          setManualPaused(true);
          setError(reason instanceof Error ? reason.message : t('player.resumeFailed'));
        });
      return;
    }

    setManualPaused(true);
    video.pause();
  }

  useImperativeHandle(forwardedRef, () => ({
    togglePlayback,
    play: () => {
      if (manualPausedRef.current || videoRef.current?.paused) togglePlayback();
    },
    pause: () => {
      if (!manualPausedRef.current && videoRef.current && !videoRef.current.paused) togglePlayback();
    },
    video: () => videoRef.current,
  }));

  useEffect(() => {
    const updateFullscreen = () => {
      const root = rootRef.current;
      const fullscreenElement = document.fullscreenElement;
      setIsFullscreen(Boolean(root && fullscreenElement && (fullscreenElement === root || fullscreenElement.contains(root) || root.contains(fullscreenElement))));
    };
    document.addEventListener('fullscreenchange', updateFullscreen);
    updateFullscreen();
    return () => document.removeEventListener('fullscreenchange', updateFullscreen);
  }, []);

  useEffect(() => () => clearControlsHideTimer(), []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) {
      setStatus('idle');
      setStatusMessage(undefined);
      setError(undefined);
      return;
    }

    if (!mpegts.isSupported()) {
      setStatus('error');
      setError(t('player.unsupported'));
      setDiagnostic(featureSummary);
      return;
    }

    let disposed = false;
    let retryTimer: number | undefined;
    let retryScheduled = false;
    let mediaInfoSummary: string | undefined;

    setStatus('connecting');
    setStatusMessage(retryAttemptRef.current > 0
      ? t('player.retrying', { attempt: retryAttemptRef.current + 1 })
      : t('player.connecting'));
    setError(undefined);

    const absoluteUrl = resolveMediaUrl(url, mediaShardKey);
    const player = mpegts.createPlayer(
      {
        type: 'flv',
        isLive,
        url: absoluteUrl,
        withCredentials: true,
        hasVideo: true,
        hasAudio: !videoOnly,
      },
      {
        enableWorker: true,
        enableStashBuffer: !isLive,
        stashInitialSize: isLive ? 128 : 384,
        lazyLoad: false,
        autoCleanupSourceBuffer: true,
        liveBufferLatencyChasing: isLive,
      },
    );

    const destroyPlayer = () => {
      try {
        player.pause();
        player.unload();
        player.detachMediaElement();
        player.destroy();
      } catch {
        // 销毁阶段不阻塞 React 卸载或重试。
      }
    };

    const tryResumePlayback = () => {
      if (disposed || manualPausedRef.current || document.visibilityState !== 'visible') return;
      void Promise.resolve(video.play())
        .then(() => {
          if (disposed) return;
          setStatus('playing');
          setStatusMessage(undefined);
          setError(undefined);
        })
        .catch((reason: unknown) => {
          if (disposed) return;
          const message = reason instanceof Error ? reason.message : String(reason ?? '');
          if (message.includes('paused to save power') || message.includes('background media')) {
            setStatus('connecting');
            setStatusMessage(t('player.backgroundPaused'));
            setError(undefined);
          }
        });
    };

    const onVisibilityChange = () => tryResumePlayback();
    const onWindowFocus = () => tryResumePlayback();
    const onVideoPause = () => {
      if (manualPausedRef.current || retryScheduled) return;
      if (!disposed && document.visibilityState === 'visible') {
        window.setTimeout(tryResumePlayback, 250);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onWindowFocus);
    video.addEventListener('pause', onVideoPause);

    const onMediaInfo = (info: unknown) => {
      mediaInfoSummary = summarizeMediaInfo(info);
      if (mediaInfoSummary) {
        setDiagnostic([label ? `source=${label}` : undefined, mediaInfoSummary, featureSummary]
          .filter(Boolean)
          .join(' | '));
      }
    };

    const onError = (type: unknown, detail: unknown, info: unknown) => {
      if (disposed || retryScheduled) return;

      const infoText = stringifyUnknown(info);
      const detailText = String(detail ?? 'Unknown');
      const diagnosis = [
        label ? `source=${label}` : undefined,
        `type=${String(type ?? 'Unknown')}`,
        `detail=${detailText}`,
        infoText ? `info=${infoText}` : undefined,
        mediaInfoSummary,
        featureSummary,
        `mediaOrigin=${new URL(absoluteUrl).origin}`,
        mediaShardBases.length > 0 ? `mediaShards=${mediaShardBases.length}` : 'mediaShards=disabled',
        videoOnly ? 'audio=disabled-by-oapm' : undefined,
      ].filter(Boolean).join(' | ');
      setDiagnostic(diagnosis);

      const audioCodecFailure = !videoOnly &&
        isCodecUnsupported(detail) &&
        String(infoText ?? '').toLocaleLowerCase().includes('audio codec');
      if (audioCodecFailure) {
        retryScheduled = true;
        setStatus('connecting');
        setStatusMessage(t('player.audioFallback'));
        setError(undefined);
        destroyPlayer();
        onAudioUnsupportedRef.current?.(diagnosis);
        return;
      }

      if (isHttpStatusCodeInvalid(detail) && retryAttemptRef.current < retryDelaysMs.length) {
        const delay = retryDelaysMs[retryAttemptRef.current] ?? retryDelaysMs.at(-1) ?? 1_000;
        retryAttemptRef.current += 1;
        retryScheduled = true;
        setStatus('connecting');
        setStatusMessage(t('player.retryLater', { delay }));
        destroyPlayer();
        retryTimer = window.setTimeout(() => {
          if (!disposed) setRestartToken((current) => current + 1);
        }, delay);
        return;
      }

      setStatus('error');
      setStatusMessage(undefined);
      if (isCodecUnsupported(detail)) {
        setError(t('player.codecUnsupported'));
      } else {
        setError(t('player.mediaError', { detail: detailText }));
      }
    };

    player.on(mpegts.Events.MEDIA_INFO, onMediaInfo);
    player.on(mpegts.Events.ERROR, onError);
    player.attachMediaElement(video);
    video.muted = muted;
    video.volume = Math.min(1, Math.max(0, volume));
    video.defaultPlaybackRate = Math.min(4, Math.max(0.25, playbackRate));
    video.playbackRate = video.defaultPlaybackRate;
    player.load();

    void Promise.resolve(player.play())
      .then(() => {
        if (disposed) return;
        retryAttemptRef.current = 0;
        manualPausedRef.current = false;
        setManuallyPaused(false);
        onPlaybackPausedChangeRef.current?.(false);
        setStatus('playing');
        setStatusMessage(undefined);
      })
      .catch((reason: unknown) => {
        if (disposed || retryScheduled) return;
        const message = reason instanceof Error ? reason.message : t('player.playFailed');
        if (message.includes('paused to save power') || message.includes('background media')) {
          setStatus('connecting');
          setStatusMessage(t('player.backgroundPaused'));
          setError(undefined);
          return;
        }
        setStatus('error');
        setStatusMessage(undefined);
        setError(message);
      });

    return () => {
      disposed = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onWindowFocus);
      video.removeEventListener('pause', onVideoPause);
      destroyPlayer();
    };
  }, [featureSummary, isLive, label, mediaShardKey, restartToken, url, videoOnly]);

  const exitFullscreen = () => {
    if (onExitFullscreen) {
      onExitFullscreen();
      return;
    }
    if (document.fullscreenElement) void document.exitFullscreen();
  };

  const hasExtendedControls = isFullscreen && Boolean(
    onPrevious || onNext || onSkipBackward || onSkipForward || onStop,
  );

  return (
    <div
      ref={rootRef}
      className={`oapm-player ${className ?? ''} ${controlsVisible ? 'player-controls-visible' : ''} ${isFullscreen ? 'player-is-fullscreen' : ''}`}
      onPointerEnter={handlePointerMove}
      onPointerMove={handlePointerMove}
      onPointerLeave={hidePlaybackControls}
      onFocusCapture={revealPlaybackControls}
    >
      <video ref={videoRef} muted={muted} playsInline autoPlay />

      {showPlaybackToggle && url && status === 'playing' && (
        <div
          className={`player-control-bar ${hasExtendedControls ? 'player-control-bar-extended' : ''}`}
          aria-hidden={!controlsVisible && !manuallyPaused}
          onPointerDown={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          <div className="player-control-cluster player-control-navigation">
            {isFullscreen && onPrevious && (
              <button type="button" disabled={previousDisabled} aria-label={t('common.previous')} title={t('common.previous')} onClick={(event) => { event.stopPropagation(); onPrevious(); }}>
                <span aria-hidden="true">⏮</span>
              </button>
            )}
            {isFullscreen && onSkipBackward && (
              <button type="button" disabled={skipDisabled} aria-label={t('common.back10')} title={t('common.back10')} onClick={(event) => { event.stopPropagation(); onSkipBackward(); }}>
                <span aria-hidden="true">↶<small>10</small></span>
              </button>
            )}
          </div>

          <button
            type="button"
            className="player-playback-toggle"
            aria-label={manuallyPaused ? t('common.play') : t('common.pause')}
            aria-pressed={manuallyPaused}
            title={manuallyPaused ? t('common.play') : t('common.pause')}
            onClick={(event) => {
              event.stopPropagation();
              togglePlayback();
            }}
          >
            <span
              className={manuallyPaused ? 'player-icon-play' : 'player-icon-pause'}
              aria-hidden="true"
            />
          </button>

          <div className="player-control-cluster player-control-navigation">
            {isFullscreen && onSkipForward && (
              <button type="button" disabled={skipDisabled} aria-label={t('common.forward10')} title={t('common.forward10')} onClick={(event) => { event.stopPropagation(); onSkipForward(); }}>
                <span aria-hidden="true"><small>10</small>↷</span>
              </button>
            )}
            {isFullscreen && onNext && (
              <button type="button" disabled={nextDisabled} aria-label={t('common.next')} title={t('common.next')} onClick={(event) => { event.stopPropagation(); onNext(); }}>
                <span aria-hidden="true">⏭</span>
              </button>
            )}
          </div>

          {isFullscreen && onStop && (
            <button type="button" className="player-control-stop" disabled={stopDisabled} aria-label={t('common.stop')} title={t('common.stop')} onClick={(event) => { event.stopPropagation(); onStop(); }}>
              <span aria-hidden="true">■</span>
            </button>
          )}

        </div>
      )}

      {isFullscreen && showPlaybackToggle && url && status === 'playing' && (
        <button
          type="button"
          className="player-fullscreen-exit"
          aria-label={t('common.exitFullscreen')}
          title={t('common.exitFullscreen')}
          onPointerDown={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            exitFullscreen();
          }}
        >
          <span aria-hidden="true">⤢</span>
        </button>
      )}

      {manuallyPaused && status === 'playing' && (
        <div className="player-paused-indicator" aria-live="polite">{t('player.paused')}</div>
      )}
      {status !== 'playing' && (
        <div className={`player-state player-state-${status}`}>
          <div className="player-state-copy">
            <span>
              {status === 'idle' && t('player.idle')}
              {status === 'connecting' && (statusMessage ?? t('player.connecting'))}
              {status === 'error' && (error ?? t('player.playFailed'))}
            </span>
            {status === 'error' && diagnostic && (
              <details className="player-diagnostic">
                <summary>{t('player.diagnostics')}</summary>
                <code>{diagnostic}</code>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
